import { useNavigate } from "@tanstack/react-router";
import {
  getControlCategoryName,
  getControlName,
  pluralize,
  useAuthConfig,
  useDebounceState,
} from "@theprelude/core";
import { ToggleGroup, ToggleItem } from "@theprelude/ds";
import { useMemo } from "react";
import { z } from "zod";
import { SCMTableFilterInput } from "@/components/table/filter-input";
import { fuzzyFilter } from "@/lib/fuzzy-filters";
import { Exception, exceptionsQuery } from "@/queries/exceptions";
import { DefaultOverlay } from "../../default-overlay";
import { AddExceptionDropdown } from "../add-exception-dropdown";
import { ExceptionsList } from "../exceptions-list";

export const exceptionsSchema = z.tuple([
  z.literal("exceptions"),
  z.object({
    filter: z.enum(["policies", "objects", "all"]),
  }),
]);

export type ExceptionsSchema = z.infer<typeof exceptionsSchema>[1];

export type ExceptionsFilter = ExceptionsSchema["filter"];

export function ExceptionsOverlay({ filter }: ExceptionsSchema) {
  const navigate = useNavigate();
  const authConfig = useAuthConfig();

  const searchFilter = useDebounceState("");
  const searchValue = searchFilter.debouncedValue.toLowerCase();

  const { data, isPending } = exceptionsQuery(authConfig).useQuery();

  const exceptions = useMemo<Exception[]>(() => {
    const types: Exception["type"][] = [];
    if (filter === "all" || filter === "objects") {
      types.push("object");
    }
    if (filter === "all" || filter === "policies") {
      types.push("policy");
    }
    return (data ?? []).filter((exception) => {
      if (!types.includes(exception.type)) {
        return false;
      }
      if (
        exception.type === "object" &&
        fuzzyFilter(exception.name ?? "", searchValue)
      ) {
        return true;
      }
      if (
        exception.type === "policy" &&
        (fuzzyFilter(getControlCategoryName(exception.category), searchValue) ||
          fuzzyFilter(getControlName(exception.control), searchValue) ||
          fuzzyFilter(exception.policy_name, searchValue))
      ) {
        return true;
      }
      if (exception.expires && fuzzyFilter(exception.expires, searchValue)) {
        return true;
      }
      return false;
    });
  }, [data, filter, searchValue]);

  const count = exceptions.length;

  return (
    <DefaultOverlay title="Exceptions">
      <div className="flex h-full flex-col gap-4 p-4 pb-0">
        <ToggleGroup
          value={filter}
          onValueChange={(value) => {
            void navigate({
              to: window.location.pathname,
              params: (prev) => prev,
              search: (prev) => ({
                ...prev,
                overlay: ["exceptions", { filter: value }],
              }),
            });
          }}
        >
          <ToggleItem value={"all" satisfies ExceptionsFilter}>All</ToggleItem>
          <ToggleItem value={"policies" satisfies ExceptionsFilter}>
            Policies
          </ToggleItem>
          <ToggleItem value={"objects" satisfies ExceptionsFilter}>
            Objects
          </ToggleItem>
        </ToggleGroup>
        <SCMTableFilterInput
          value={searchFilter.value}
          onChange={(value) => searchFilter.setDelayed(value)}
          onClear={() => searchFilter.setImmediate("")}
        />
        <div className="flex items-center justify-between text-xs">
          <span>
            {count.toLocaleString()} {pluralize(count, "exception")}
          </span>
          <AddExceptionDropdown />
        </div>
        <ExceptionsList exceptions={exceptions} loading={isPending} />
      </div>
    </DefaultOverlay>
  );
}
